// PatchyHub — export a documented sub-account into PatchyHub's asset shape.
//
// ROUND 1 IS A SIMULATION, AND THE SCREEN NEVER PRETENDS OTHERWISE. Every number here comes from
// really documented entities and the payload shown is the real one we would POST, but nothing is
// transmitted: there is no PatchyHub credential and no destination id yet. The banner, the button
// label, the run rows and the diagnostic file all carry that fact, because the failure mode of a
// convincing demo is someone believing data moved.
//
// The step order deliberately mirrors PatchyHub's own importer (Connect → Assets → Destination →
// Import) so the muscle memory transfers, with Document inserted where their extension does its
// token handoff — that substitution IS the pitch.
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { PatchyPlan, PatchyRun, PatchyRunResult } from '../../lib/agencyApi';
import { agencyApi } from '../../lib/agencyApi';
import type { SubAccount } from '../../lib/suite-types';
import { Button, Card, Chip, EmptyNote, ErrorNote, Spinner, StatTile } from './ui';
import { formatApiTime, timeAgo } from '../../lib/time';

const STEPS = ['Sub-account', 'Document', 'Assets', 'Destination', 'Sync'] as const;
type StepIdx = 0 | 1 | 2 | 3 | 4;

const label = (a: SubAccount) => a.client_name || a.location_id;

// ── step header ────────────────────────────────────────────────────
function Steps({ at }: { at: StepIdx }) {
  return (
    <ol className="flex flex-wrap items-center gap-x-3 gap-y-2">
      {STEPS.map((s, i) => {
        const done = i < at;
        const now = i === at;
        return (
          <li key={s} className="flex items-center gap-3">
            <span className="flex items-center gap-2">
              <span
                className={`grid h-6 w-6 place-items-center rounded-full text-[11px] font-bold ${
                  done ? 'bg-emerald-600 text-white' : now ? 'bg-brand text-brand-fg' : 'bg-slate-200 text-slate-500'
                }`}
              >
                {done ? '✓' : i + 1}
              </span>
              <span className={`text-sm ${now ? 'font-semibold text-slate-900' : 'text-slate-500'}`}>{s}</span>
            </span>
            {i < STEPS.length - 1 && <span className="h-px w-8 bg-slate-200" aria-hidden="true" />}
          </li>
        );
      })}
    </ol>
  );
}

// The simulation's log line, shaped like their per-asset-type import log.
interface LogLine { type: string; count: number; state: 'pending' | 'running' | 'done' }

export default function PatchyHub() {
  const [accounts, setAccounts] = useState<SubAccount[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [picked, setPicked] = useState<SubAccount | null>(null);
  const [step, setStep] = useState<StepIdx>(0);

  const [plan, setPlan] = useState<PatchyPlan | null>(null);
  const [planning, setPlanning] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [skip, setSkip] = useState<Set<string>>(new Set());

  const [systemName, setSystemName] = useState('');
  const [log, setLog] = useState<LogLine[] | null>(null);
  const [result, setResult] = useState<PatchyRunResult | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [runs, setRuns] = useState<PatchyRun[]>([]);
  const [showPayload, setShowPayload] = useState(false);

  useEffect(() => {
    agencyApi.accounts().then(setAccounts).catch((e: Error) => setError(e.message));
    agencyApi.patchyRuns().then(setRuns).catch(() => {});
  }, []);

  const loadPlan = useCallback(async (a: SubAccount) => {
    setPlanning(true);
    setError(null);
    try {
      setPlan(await agencyApi.patchyPlan(a.location_id));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setPlanning(false);
    }
  }, []);

  function choose(a: SubAccount) {
    setPicked(a);
    setPlan(null);
    setResult(null);
    setLog(null);
    setSkip(new Set());
    setSystemName(`${label(a)} — documented`);
    setStep(1);
    void loadPlan(a);
  }

  async function documentNow() {
    if (!picked) return;
    setScanning(true);
    setError(null);
    try {
      await agencyApi.runScan(picked.location_id);
      await loadPlan(picked);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setScanning(false);
    }
  }

  // Types the operator kept. Skipping is local to the run — nothing is deleted.
  const included = useMemo(
    () => (plan?.by_type ?? []).filter((t) => !skip.has(t.tssType)),
    [plan, skip],
  );
  const includedCount = included.reduce((n, t) => n + t.count, 0);

  /**
   * The simulation. Paced per asset type because that is how their importer actually behaves —
   * one POST per type, roughly 3s apart — so the timing teaches the real shape rather than just
   * filling a progress bar. The RUN ITSELF is recorded server-side first, so the run id and the
   * tallies on screen are real rows, not numbers invented by this component.
   */
  async function runSync() {
    if (!picked) return;
    setSyncing(true);
    setError(null);
    setStep(4);
    const lines: LogLine[] = included.map((t) => ({ type: t.patchyType, count: t.count, state: 'pending' }));
    setLog(lines);
    try {
      const res = await agencyApi.patchySync(picked.location_id, systemName.trim() || label(picked));
      for (let i = 0; i < lines.length; i++) {
        setLog((prev) => prev?.map((l, j) => (j === i ? { ...l, state: 'running' } : l)) ?? null);
        await new Promise((r) => setTimeout(r, 260));
        setLog((prev) => prev?.map((l, j) => (j === i ? { ...l, state: 'done' } : l)) ?? null);
      }
      setResult(res);
      setRuns(await agencyApi.patchyRuns());
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSyncing(false);
    }
  }

  async function downloadDiagnostic(runId: string) {
    try {
      const d = await agencyApi.patchyDiagnostic(runId);
      const blob = new Blob([JSON.stringify(d, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `patchyhub-run-${runId.slice(0, 8)}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError((e as Error).message);
    }
  }

  function restart() {
    setPicked(null); setPlan(null); setResult(null); setLog(null); setStep(0);
  }

  return (
    <div className="space-y-5">
      {/* The honesty banner. First thing on the page, every time — not a footnote. */}
      <Card className="border-amber-200 bg-amber-50 p-4">
        <div className="flex flex-wrap items-start gap-3">
          <span className="text-lg leading-none">🧪</span>
          <div className="text-sm text-amber-900">
            <span className="font-semibold">Simulation — nothing is sent to PatchyHub.</span>{' '}
            The account is really documented and the payload below is the real one we would POST, but
            delivery isn't built: there's no PatchyHub credential and no destination id yet. Every run
            here is recorded as <code className="rounded bg-amber-100 px-1">simulated</code>.
          </div>
        </div>
      </Card>

      {error && <ErrorNote error={error} />}

      <Card className="p-5">
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
          <Steps at={step} />
          {picked && (
            <Button variant="ghost" onClick={restart}>Start over</Button>
          )}
        </div>

        {/* ── 1 · Sub-account ── */}
        {step === 0 && (
          <div className="space-y-3">
            <div>
              <h2 className="text-base font-semibold text-slate-900">Which sub-account?</h2>
              <p className="text-sm text-slate-500">
                Pick the account to document and send. One at a time for now.
              </p>
            </div>
            {!accounts && <Spinner label="Loading sub-accounts…" />}
            {accounts && accounts.length === 0 && (
              <EmptyNote>No sub-accounts yet. Sync your roster from Sub-Accounts first.</EmptyNote>
            )}
            {accounts && accounts.length > 0 && (
              <ul className="divide-y divide-slate-100 overflow-hidden rounded-xl border border-slate-200">
                {accounts.filter((a) => a.status === 'active').map((a) => (
                  <li key={a.location_id}>
                    <button
                      onClick={() => choose(a)}
                      className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-slate-50"
                    >
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium text-slate-800">{label(a)}</span>
                        <span className="block text-xs text-slate-400">
                          {a.last_scan_at ? `Documented ${timeAgo(a.last_scan_at)}` : 'Never documented'}
                        </span>
                      </span>
                      {a.last_scan_at
                        ? <Chip label="Documented" color="#16a34a" />
                        : <Chip label="Needs a scan" muted />}
                      <span className="text-slate-300">›</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* ── 2 · Document ── */}
        {step === 1 && picked && (
          <div className="space-y-4">
            <div>
              <h2 className="text-base font-semibold text-slate-900">Document {label(picked)}</h2>
              <p className="text-sm text-slate-500">
                This is where PatchyHub's extension does its token handoff. We read what we already
                documented instead — no extension, no 5-minute token window.
              </p>
            </div>

            {planning && <Spinner label="Reading the documentation…" />}

            {!planning && plan && !plan.documented && (
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-5 text-center">
                <p className="text-sm text-slate-600">
                  We haven't documented this account yet, so there's nothing to send.
                </p>
                <div className="mt-3">
                  <Button onClick={documentNow} disabled={scanning}>
                    {scanning ? 'Documenting…' : 'Document it now'}
                  </Button>
                </div>
              </div>
            )}

            {!planning && plan && plan.documented && (
              <>
                <div className="grid gap-3 sm:grid-cols-3">
                  <StatTile label="Documented assets" value={plan.totals.entities} />
                  <StatTile label="Exportable" value={plan.totals.exportable} hint="have a PatchyHub type" />
                  <StatTile
                    label="Last documented"
                    value={plan.documented_at ? timeAgo(plan.documented_at) : '—'}
                    hint={plan.documented_at ? formatApiTime(plan.documented_at) : undefined}
                  />
                </div>
                <div className="flex items-center gap-3">
                  <Button onClick={() => setStep(2)}>Continue</Button>
                  <Button variant="secondary" onClick={documentNow} disabled={scanning}>
                    {scanning ? 'Re-documenting…' : 'Re-document first'}
                  </Button>
                </div>
              </>
            )}
          </div>
        )}

        {/* ── 3 · Assets ── */}
        {step === 2 && plan && (
          <div className="space-y-4">
            <div>
              <h2 className="text-base font-semibold text-slate-900">What gets sent</h2>
              <p className="text-sm text-slate-500">
                Each row is one <code className="rounded bg-slate-100 px-1 text-xs">POST /rest/v1/assets</code>{' '}
                batch, exactly how their importer sends it.
              </p>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wide text-slate-400">
                    <th className="w-10 py-2"></th>
                    <th className="py-2 pr-4 font-semibold">Ours</th>
                    <th className="py-2 pr-4 font-semibold">PatchyHub asset_type</th>
                    <th className="py-2 pr-4 text-right font-semibold">Count</th>
                  </tr>
                </thead>
                <tbody>
                  {plan.by_type.map((t) => {
                    const on = !skip.has(t.tssType);
                    return (
                      <tr key={t.tssType} className="border-t border-slate-100">
                        <td className="py-2">
                          <input
                            type="checkbox"
                            checked={on}
                            aria-label={`Include ${t.tssType}`}
                            onChange={() => setSkip((prev) => {
                              const next = new Set(prev);
                              if (on) next.add(t.tssType); else next.delete(t.tssType);
                              return next;
                            })}
                          />
                        </td>
                        <td className="py-2 pr-4 text-slate-700">{t.tssType.replace(/_/g, ' ')}</td>
                        <td className="py-2 pr-4">
                          <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-700">{t.patchyType}</code>
                          {t.expansion && (
                            <span className="ml-2 text-[11px] text-slate-400">
                              they create these by expanding a container
                            </span>
                          )}
                        </td>
                        <td className="py-2 pr-4 text-right tabular-nums text-slate-700">{t.count}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {plan.dropped.length > 0 && (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
                <span className="font-semibold">Not sent — PatchyHub has no type for these:</span>{' '}
                {plan.dropped.map((d) => `${d.tssType.replace(/_/g, ' ')} (${d.count})`).join(', ')}.
                They stay documented here.
              </div>
            )}
            {plan.unnamed > 0 && (
              <p className="text-xs text-amber-700">
                {plan.unnamed} asset{plan.unnamed === 1 ? '' : 's'} have no name and would arrive as
                “Untitled …”. Their UI lists assets by name, so worth fixing first.
              </p>
            )}

            <div className="flex flex-wrap items-center gap-3">
              <Button onClick={() => setStep(3)} disabled={includedCount === 0}>
                Continue ({includedCount} asset{includedCount === 1 ? '' : 's'})
              </Button>
              <Button variant="ghost" onClick={() => setShowPayload((v) => !v)}>
                {showPayload ? 'Hide' : 'Show'} the actual payload
              </Button>
            </div>

            {showPayload && plan.sample && (
              <div>
                <p className="mb-1 text-xs text-slate-500">
                  One real row from this account — this is what would be POSTed. Note{' '}
                  <code className="rounded bg-slate-100 px-1">metadata.links</code>: theirs comes back
                  empty on import, ours is populated.
                </p>
                <pre className="max-h-72 overflow-auto rounded-lg bg-slate-900 p-3 text-[11px] leading-relaxed text-slate-100">
{JSON.stringify(plan.sample, null, 2)}
                </pre>
              </div>
            )}
          </div>
        )}

        {/* ── 4 · Destination ── */}
        {step === 3 && (
          <div className="space-y-4">
            <div>
              <h2 className="text-base font-semibold text-slate-900">Choose destination</h2>
              <p className="text-sm text-slate-500">
                PatchyHub calls the container a <b>System</b>. Live, this creates one via{' '}
                <code className="rounded bg-slate-100 px-1 text-xs">POST /rest/v1/systems</code> and
                uses the id it returns as <code className="rounded bg-slate-100 px-1 text-xs">module_id</code>.
              </p>
            </div>
            <div className="max-w-md">
              <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">
                System name
              </label>
              <input
                value={systemName}
                onChange={(e) => setSystemName(e.target.value)}
                placeholder="e.g. My Agency CRM"
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-brand focus:ring-1 focus:ring-brand"
              />
            </div>
            <div className="flex items-center gap-3">
              <Button onClick={runSync} disabled={syncing}>
                Sync to PatchyHub for Documentation
              </Button>
              <span className="text-xs text-slate-400">Simulated — nothing leaves this Worker.</span>
            </div>
          </div>
        )}

        {/* ── 5 · Sync ── */}
        {step === 4 && (
          <div className="space-y-4">
            <div>
              <h2 className="text-base font-semibold text-slate-900">
                {result ? 'Simulated run complete' : 'Running…'}
              </h2>
              <p className="text-sm text-slate-500">One batch per asset type, in send order.</p>
            </div>

            {log && (
              <ul className="divide-y divide-slate-100 overflow-hidden rounded-xl border border-slate-200">
                {log.map((l) => (
                  <li key={l.type} className="flex items-center gap-3 px-4 py-2 text-sm">
                    <span className="w-5 text-center">
                      {l.state === 'done' ? '✅' : l.state === 'running' ? '⏳' : '·'}
                    </span>
                    <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-700">{l.type}</code>
                    <span className="ml-auto tabular-nums text-slate-500">{l.count}</span>
                  </li>
                ))}
              </ul>
            )}

            {result && (
              <>
                <div className="grid gap-3 sm:grid-cols-4">
                  <StatTile label="Would create" value={result.totals.exportable} />
                  <StatTile label="Scanned" value={result.totals.entities} />
                  <StatTile label="Not sent" value={result.totals.dropped} hint="no PatchyHub type" />
                  <StatTile label="Actually sent" value={result.sent ? 'yes' : 'none'} hint="simulation" />
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <span className="text-xs text-slate-500">
                    Run <code className="rounded bg-slate-100 px-1">{result.run_id.slice(0, 8)}</code>
                  </span>
                  <Button variant="secondary" onClick={() => downloadDiagnostic(result.run_id)}>
                    ↓ Diagnostic file
                  </Button>
                  <Button variant="ghost" onClick={restart}>Run another</Button>
                </div>
              </>
            )}
          </div>
        )}
      </Card>

      {/* Run history */}
      {runs.length > 0 && (
        <Card className="p-5">
          <h2 className="mb-3 text-base font-semibold text-slate-900">Recent runs</h2>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-slate-400">
                  <th className="py-2 pr-4 font-semibold">Run</th>
                  <th className="py-2 pr-4 font-semibold">System</th>
                  <th className="py-2 pr-4 font-semibold">Mode</th>
                  <th className="py-2 pr-4 text-right font-semibold">Assets</th>
                  <th className="py-2 pr-4 font-semibold">When</th>
                  <th className="py-2"></th>
                </tr>
              </thead>
              <tbody>
                {runs.map((r) => (
                  <tr key={r.id} className="border-t border-slate-100">
                    <td className="py-2 pr-4"><code className="text-xs text-slate-500">{r.id.slice(0, 8)}</code></td>
                    <td className="py-2 pr-4 text-slate-700">{r.system_name ?? '—'}</td>
                    <td className="py-2 pr-4">
                      {r.mode === 'live'
                        ? <Chip label="Live" color="#16a34a" />
                        : <Chip label="Simulated" color="#d97706" />}
                    </td>
                    <td className="py-2 pr-4 text-right tabular-nums text-slate-700">{r.created}</td>
                    <td className="py-2 pr-4 text-xs text-slate-400">{timeAgo(r.created_at)}</td>
                    <td className="py-2 text-right">
                      <button
                        onClick={() => downloadDiagnostic(r.id)}
                        className="text-xs font-medium text-brand hover:underline"
                      >
                        Diagnostic
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
